import { CalendarPlusIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  type CalendarMomentPrefill,
  articleMomentPrefill,
  articlePostBlocker,
  articlePostPrompt,
  publicUrl,
} from './article-promotion';

import { CalendarMomentDialog } from '@/components/calendar/CalendarMomentDialog';
import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import { Button } from '@/components/ui/button';
import type { ArticlePublicationSummary } from '@/hooks/reach/useArticleHandoff';
import { buildAgentUrl } from '@/lib/agent-links/buildAgentUrl';
import { ITEM_META } from '@/lib/ui-tokens';

/**
 * The two ways a finished article leaves `/reach/editorial` for the rest of
 * Mora: a LinkedIn post drafted in the composer (where it is checked, sent for
 * approval and scheduled like every other post), and a dated moment on the
 * Publishing calendar. Rules and their reasons: `article-promotion.ts`.
 */
export function ArticlePromoteActions({
  topicId,
  title,
  publication,
  wrongLawCount,
  needsVerifiedRewrite,
}: {
  topicId: string;
  title: string;
  /** Latest non-cancelled publication; undefined while unknown or never handed off. */
  publication: ArticlePublicationSummary | undefined;
  wrongLawCount: number;
  needsVerifiedRewrite: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const [moment, setMoment] = React.useState<CalendarMomentPrefill | null>(null);
  const publishedUrl = publication?.publishedUrl ?? null;
  const blocker = articlePostBlocker({ publishedUrl, wrongLawCount, needsVerifiedRewrite });
  const url = publicUrl(publishedUrl);

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={Boolean(blocker)}
          aria-describedby={blocker ? `post-blocker-${topicId}` : undefined}
          onClick={() => {
            if (!url) return;
            navigate(
              buildAgentUrl({
                prompt: articlePostPrompt({ title, url }),
                source: { type: 'article', id: topicId },
                platforms: ['linkedin'],
                newThread: true,
              }),
            );
          }}
        >
          <PlatformBrandIcon platform="linkedin" />
          Draft LinkedIn post
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setMoment(
              articleMomentPrefill({
                title,
                publishedUrl,
                publishedAt: publication?.publishedAt ?? null,
                scheduledAt: publication?.scheduledAt ?? null,
              }),
            )
          }
        >
          <CalendarPlusIcon weight="regular" />
          Add to calendar
        </Button>
      </div>
      {blocker ? (
        <p id={`post-blocker-${topicId}`} className={ITEM_META}>
          {blocker}
        </p>
      ) : null}
      <CalendarMomentDialog
        subject={moment ? moment.startsOn : null}
        prefill={moment}
        onClose={() => setMoment(null)}
      />
    </div>
  );
}
